import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { capture } from "@/lib/analytics";
import confetti from "canvas-confetti";
import { Sparkles, CheckCircle2 } from "lucide-react";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [error, setError] = useState(null);
  const attempts = useRef(0);
  const fired = useRef(false);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      navigate("/pricing");
      return;
    }
    let active = true;

    const poll = async () => {
      if (attempts.current >= 8 || !active) {
        if (active) setStatus("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const r = await apiClient.get(`/billing/status/${sessionId}`);
        if (!active) return;
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          if (!fired.current) {
            fired.current = true;
            // CONVERSION EVENT — closes the funnel: pricing_viewed → mastery_purchase_started → checkout_completed
            capture("checkout_completed", {
              session_id: sessionId,
              plan: r.data.plan || null,
              tier: r.data.tier || null,
              amount: r.data.amount_total ? r.data.amount_total / 100 : null,
              currency: r.data.currency || null,
            });
            confetti({ particleCount: 180, spread: 90, origin: { y: 0.5 }, colors: ["#e05d3a", "#2a9d8f", "#f4a261"] });
          }
          return;
        }
        if (r.data.status === "expired") {
          setStatus("expired");
          capture("checkout_expired", { session_id: sessionId });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not verify payment");
        setStatus("error");
      }
    };
    poll();
    return () => {
      active = false;
    };
  }, [sessionId, navigate, refresh]);

  return (
    <div className="App min-h-screen">
      <Header />
      <main className="max-w-2xl mx-auto px-4 md:px-8 py-20 text-center">
        {status === "polling" && (
          <>
            <span className="spinner" />
            <h1 className="font-display text-4xl mt-5 leading-tight tracking-tight">Confirming payment…</h1>
            <p className="text-[var(--ink-2)] mt-2 text-sm">Hold tight, this takes a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={56} className="mx-auto text-[var(--emerald)]" />
            <h1 className="font-display text-6xl mt-5 leading-[1.05] tracking-tight" data-testid="billing-success-title">
              You're <em>in</em>.
            </h1>
            <p className="mt-4 text-[var(--ink-2)]">
              Your plan is active. Time to put it to work.
            </p>
            <Link to="/dashboard" className="btn-accent mt-7" data-testid="billing-success-dashboard">
              <Sparkles size={14} /> Open dashboard
            </Link>
          </>
        )}
        {(status === "timeout" || status === "expired" || status === "error") && (
          <>
            <h1 className="font-display text-4xl mt-5 tracking-tight">Hmm — that didn't go through.</h1>
            <p className="text-[var(--ink-2)] mt-2">{error || "Try again or contact support."}</p>
            <Link to="/pricing" className="btn-ghost mt-6 inline-flex">Back to pricing</Link>
          </>
        )}
      </main>
    </div>
  );
}
